import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If already signed in, redirect based on user type
    if (user) {
      if (user.email === 'moein@udaadu.com') {
        navigate('/admin-dashboard');
      } else {
        navigate('/my-dashboard');
      }
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await signIn(email, password);
    
    if (error) {
      setError(error.message || 'Sign in failed');
      setLoading(false);
      return;
    }
    
    // Redirect based on user type
    if (email === 'moein@udaadu.com') {
      navigate('/admin-dashboard');
    } else {
      navigate('/my-dashboard');
    }
    
    setLoading(false);
  };

  const handleGoToChecklist = () => {
    navigate('/ar');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-4">
      <div
        className="w-full max-w-md p-8 rounded-2xl"
        style={{
          backgroundColor: "#0a0a0a",
          boxShadow: "12px 12px 24px #000000, -12px -12px 24px #141414",
        }}
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#E4683B] mb-2">Welcome Back</h1>
          <p className="text-gray-400">Sign in to access your ADU dashboard</p>
        </div>

        <form onSubmit={handleSignIn} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <div className="relative">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E4683B]"
                style={{
                  backgroundColor: "#141414",
                  boxShadow: "inset 4px 4px 8px #000000, inset -4px -4px 8px #1a1a1a",
                }}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E4683B]"
                style={{
                  backgroundColor: "#141414",
                  boxShadow: "inset 4px 4px 8px #000000, inset -4px -4px 8px #1a1a1a",
                }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-400 p-3 bg-red-500/10 rounded-xl">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-300 disabled:opacity-50"
            style={{
              backgroundColor: "#E4683B",
              boxShadow: "4px 4px 8px #000000, -4px -4px 8px #141414",
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <button
            type="button"
            onClick={handleGoToChecklist}
            className="w-full py-3 rounded-xl font-semibold transition-all duration-300"
            style={{
              backgroundColor: "#f5f5f5",
              color: "#1a1a1a",
              boxShadow: "8px 8px 16px #000000, -8px -8px 16px #1a1a1a",
            }}
          >
            Go to Checklist
          </button>
        </form>

        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm">
            Don't have an account?{' '}
            <a href="/what-fits-my-lot" className="text-[#E4683B] hover:underline">
              Get a free quote
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;